import React, {useEffect, useState} from 'react';
import ImageBasic from "../components/Common/Image/ImageBasic";
import IMG from "../constants/image";
import Button from "../components/Common/Button";
import {VARIANT} from "../constants";
import {clsx} from "clsx";
import {useDeliveryStore} from "../store/deliveryStore";
import {fetchShippingProviders} from "../services/deliveryServices";
import {updateShippingProviders} from "../network/deliveryApi";
import {isEmpty} from "lodash";
import CheckboxInput from "../components/Common/Input/CheckboxInput";
import popupStore from "../store/popupStore";
import {text} from "../constants/text";
import merchantStore from "../store/merchantStore";

// Component for toggle switches
export const ToggleSwitch = ({isActive, onChange, isLockClosed, disabled = false}) => {
    const {setPopup} = popupStore()
    const handleToggle = () => {
        if (isLockClosed) {
            setPopup({
                isOpen: true,
                data: {
                    customTitle: text.selfDeliveryDisableTitle,
                    image: IMG.popupUpdateStatus,
                    accept: {
                        label: text.agree,
                        action: () => {
                            setPopup({})
                        }
                    }
                }
            });
        } else if (!disabled) onChange()
    }
    return (
        <div
            onClick={handleToggle}
            className={clsx('relative inline-block w-10 h-5 rounded-full cursor-pointer transition-colors duration-200',
                isActive ? 'bg-[#408764]' : 'bg-[#EFEFEF]',
                (isLockClosed || disabled) && 'cursor-not-allowed',
                disabled && !isActive && 'bg-[#EFEFEF]',
                disabled && isActive && 'bg-[#408764]')}
        >
            {isLockClosed && (
                <ImageBasic className='absolute top-1 right-[5px] z-10' src={IMG.icon.lockClosed} alt='clock close'/>
            )}
            <div
                className={`absolute top-1 w-3 h-3 rounded-full transition-transform duration-200 ${
                    isActive ? 'transform translate-x-6 bg-white' : 'transform translate-x-1 bg-[#C2C2C2]'
                }`}
            />
        </div>
    );
};

// Component for shipping option
const ShippingOption = ({item, onToggle, disabled}) => {
    return (
        <div className="flex justify-between items-center py-2" key={item.id}>
            <span className="text-sm">{item.name}</span>
            <ToggleSwitch
                isActive={item.enabled}
                onChange={() => onToggle(item.id, !item.enabled)}
                disabled={disabled}
            />
        </div>
    );
};

// Main component for the shipping configuration section
const ShippingConfigPage = () => {
    const {shippingProviders} = useDeliveryStore();
    const {merchantProfile, getMerchantProfile} = merchantStore() || {};

    // State for all toggles and checkboxes
    const [providers, setProviders] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [disableSelfDelivery, setDisableSelfDelivery] = useState(false);
    // Track which providers have changed
    const [changedProviders, setChangedProviders] = useState([]);

    // Initialize providers state from store
    useEffect(() => {
        if (!isEmpty(shippingProviders)) {
            setProviders([...shippingProviders]);
        }
    }, [shippingProviders]);

    // Fetch providers on component mount
    useEffect(() => {
        fetchShippingProviders();
    }, []);

    // Toggle provider enabled status
    const handleToggleProvider = (id, enabled) => {
        setProviders(prevProviders => {
            const newProviders = prevProviders.map(provider =>
                provider.id === id ? {...provider, enabled} : provider
            );
            setHasChanges(true);

            // Add this provider to the changed providers list
            setChangedProviders(prev => {
                // Remove if already exists
                const filtered = prev.filter(item => item.providerId !== id);
                // Add with the new value
                return [...filtered, {
                    providerId: id,
                    enabled: enabled
                }];
            });

            return newProviders;
        });
    };

    // Toggle seller shipping
    const handleToggleSellerShipping = () => {
        // This is locked in your UI, so no action needed
    };

    // Toggle seller delivery
    const handleToggleSellerDelivery = () => {
        setHasChanges(true);
        setDisableSelfDelivery(!disableSelfDelivery)
    };

    // Save changes
    const handleSave = async () => {
        updateShippingProviders({
            disableSelfDelivery,
            toggleItemReq: changedProviders
        }).then(response => {
            if (response.success) {
                setHasChanges(false);
                getMerchantProfile();
            } else {
                setHasChanges(false);
                setDisableSelfDelivery(!disableSelfDelivery)
                setProviders([...shippingProviders]);
            }
        });
    };

    useEffect(() => {
        if (merchantProfile?.id) setDisableSelfDelivery(!!merchantProfile?.disable_self_delivery)
    }, [merchantProfile])

    return (
        <div className="animate-fadeIn w-auto max-w-4xl mx-auto px-4 md:px-0 md:space-y-6 text-sm">
            {/* Header */}
            <div className="hidden md:flex items-center justify-center gap-2 mb-4">
                <ImageBasic src={IMG.icon.truckGreen} alt="truck"/>
                <div className="text-xl font-bold">{text.shippingConfig}</div>
            </div>

            {/* Description */}
            <div className="hidden md:block text-sm text-center">
                {text.selfDeliveryDescription}
            </div>

            {/* Seller Shipping Section */}
            <div className="p-4 bg-white rounded-lg md:rounded-xl space-y-3 md:px-[115px] mb-4 md:mb-0">
                <div className="flex justify-between items-center">
                    <div className="">{text.selfDeliveryTitle}</div>
                    <ToggleSwitch
                        isActive={true}
                        isLockClosed={true}
                        onChange={handleToggleSellerShipping}
                    />
                </div>
                <div className='flex items-center justify-start'>
                    <CheckboxInput
                        customClass='mt-1'
                        classLabel='text-xs'
                        classSublabel='italic'
                        id="sellerShipping"
                        name="sellerShipping"
                        label={text.selfDeliveryOption}
                        sublabel={text.selfDeliverySublabel}
                        checked={!disableSelfDelivery}
                        onChange={handleToggleSellerDelivery}
                    />
                </div>
            </div>

            {/* Partner Shipping Section */}
            <div className="p-4 bg-white rounded-lg md:rounded-xl space-y-4 md:px-[115px] mb-4 md:mb-0">
                <div className="">{text.partnerDeliveryTitle}</div>
                {!isEmpty(providers) && providers.map((item) => (
                    <ShippingOption
                        key={item.id}
                        item={item}
                        onToggle={handleToggleProvider}
                    />
                ))}

                {/* Info Note */}
                <div className="flex items-center gap-2 bg-[#FFFCF3] p-4 rounded-lg md:rounded-xl">
                    <ImageBasic src={IMG.icon.errorOutlineGray} alt="warning"/>
                    <div className="text-xs text-gray-600">
                        {text.shippingCostNote}
                    </div>
                </div>

                {/* Save Button */}
                <Button
                    variant={(!hasChanges) ? VARIANT.SECONDARY : VARIANT.PRIMARY}
                    onClick={handleSave}
                    className={clsx(
                        "hidden md:block w-[262px] py-2 text-base",
                        !hasChanges && "opacity-50 cursor-not-allowed"
                    )}
                    disabled={!hasChanges}
                >
                    {text.saveChanges}
                </Button>
            </div>
            {/* Save Button */}
            <Button
                variant={(!hasChanges) ? VARIANT.SECONDARY : VARIANT.PRIMARY}
                onClick={handleSave}
                className={clsx(
                    "md:hidden w-full py-2 text-base",
                    !hasChanges && "opacity-50 cursor-not-allowed"
                )}
                disabled={!hasChanges}
            >
                {text.saveChanges}
            </Button>
        </div>
    );
};

export default ShippingConfigPage;